import { useCallback, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/useAuth';
import type { ModuleEntry } from '@/lib/module-registry';
import { originFromUrl, isInboundMessage } from '@/lib/post-message-validation';

// ---------------------------------------------------------------------------
// Message schemas (documented contract between shell and micro-frontends)
// ---------------------------------------------------------------------------

/** Messages FROM shell TO micro-frontend */
interface OutboundMessage {
  type: 'AUTH_CONTEXT' | 'TOKEN_REFRESH' | 'AUTH_ERROR';
  version: '1.0';
  payload:
    | { userId: string; username: string; role: string; accessToken: string }
    | { accessToken: string }
    | { error: string };
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Manages the postMessage bridge between the App Shell and embedded
 * micro-frontend iframes.
 *
 * - Validates inbound message origins against the module's registered sourceUrl.
 * - Responds to MODULE_READY with AUTH_CONTEXT.
 * - Sends TOKEN_REFRESH when the access token changes.
 * - Cleans up listeners on unmount.
 */
export function usePostMessageBridge(module: ModuleEntry | undefined) {
  const { user, role, accessToken } = useAuth();
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const registeredOrigin = module?.sourceUrl ? originFromUrl(module.sourceUrl) : '';

  // Track the latest values so the message listener always has them.
  // Refs are updated inside useEffect to satisfy react-hooks/refs rule.
  const tokenRef = useRef(accessToken);
  const userRef = useRef(user);
  const roleRef = useRef(role);

  useEffect(() => {
    tokenRef.current = accessToken;
    userRef.current = user;
    roleRef.current = role;
  });

  // ---- Send helper ----
  const sendToIframe = useCallback(
    (message: OutboundMessage) => {
      const iframe = iframeRef.current;
      if (!iframe?.contentWindow || !registeredOrigin) return;
      iframe.contentWindow.postMessage(message, registeredOrigin);
    },
    [registeredOrigin],
  );

  // ---- Inbound message listener ----
  useEffect(() => {
    if (!registeredOrigin) return;

    function handleMessage(event: MessageEvent) {
      // Origin validation
      if (event.origin !== registeredOrigin) {
        console.warn(
          `[PostMessageBridge] Discarded message from unregistered origin: ${event.origin}`,
        );
        return;
      }

      if (!isInboundMessage(event.data)) return;

      const currentUser = userRef.current;
      const currentToken = tokenRef.current;
      const currentRole = roleRef.current;

      if (!currentUser || !currentToken) {
        sendToIframe({
          type: 'AUTH_ERROR',
          version: '1.0',
          payload: { error: 'User is not authenticated' },
        });
        return;
      }

      sendToIframe({
        type: 'AUTH_CONTEXT',
        version: '1.0',
        payload: {
          userId: currentUser.userId,
          username: currentUser.username,
          role: currentRole,
          accessToken: currentToken,
        },
      });
    }

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [registeredOrigin, sendToIframe]);

  // ---- Token refresh broadcast ----
  const prevTokenRef = useRef(accessToken);
  useEffect(() => {
    // Only send TOKEN_REFRESH when the token actually changes (not on mount)
    if (
      accessToken &&
      prevTokenRef.current &&
      accessToken !== prevTokenRef.current
    ) {
      sendToIframe({
        type: 'TOKEN_REFRESH',
        version: '1.0',
        payload: { accessToken },
      });
    }
    prevTokenRef.current = accessToken;
  }, [accessToken, sendToIframe]);

  return { iframeRef };
}
